import React from "react";
import { Box } from "@mui/material";
import { PieceVisual, pieceVisualRenderSizePx } from "../board/PieceVisual";
import { trayInnerSx } from "../styles/mainStyles";

const LIST_TRAY_PIECE_DISPLAY_SCALE = 0.88;
const LIST_TRAY_BEVEL_BLEED_PX = 1;
const LIST_TRAY_STRIP_SLACK_PX = 8;
const LIST_TRAY_SCROLLBAR_RESERVE_PX = 0;

function listTrayMetrics(cellPx, boardScale = 1) {
    const trayScale = boardScale * LIST_TRAY_PIECE_DISPLAY_SCALE;
    const pieceScreenPx = Math.round(pieceVisualRenderSizePx(cellPx) * trayScale);
    const circleOuterPx = pieceScreenPx + LIST_TRAY_BEVEL_BLEED_PX * 2;
    const stripHeightPx =
        circleOuterPx + LIST_TRAY_STRIP_SLACK_PX + LIST_TRAY_SCROLLBAR_RESERVE_PX;
    return { trayScale, circleOuterPx, stripHeightPx };
}

function chunkTrayColumns(indices, rows) {
    const rowCount = Math.max(1, rows);
    if (rowCount <= 1) return indices.map((pieceIndex) => [pieceIndex]);
    const columns = [];
    for (let i = 0; i < indices.length; i += rowCount) {
        columns.push(indices.slice(i, i + rowCount));
    }
    return columns;
}

/**
 * Piece tray for screenshot pages (static — no drag).
 * `rows` is 2 on the portrait play screen, matching the Android tray.
 * Pass `maxHeight` on wide tablets so pieces shrink to fit instead of clipping.
 */
export default function ScreenshotPiecesTray({
    pieceIndices,
    imageUrl,
    grid,
    paths,
    boardScale = 1,
    rows = 1,
    /** Skip the frosted inner panel — the parent already paints the Android tray colour. */
    bare = false,
    /** Cap total tray content height (px); scales pieces down when board is large (iPad). */
    maxHeight = null,
}) {
    if (!paths || grid <= 0 || pieceIndices.length === 0) return null;

    const rowCount = Math.max(1, Math.min(rows, pieceIndices.length));
    const cellPx = 500 / grid;
    const rowGapPx = rowCount > 1 ? 6 : 0;
    const padY = bare ? 4 : 12;
    let { trayScale, circleOuterPx } = listTrayMetrics(cellPx, boardScale);
    let trayHeightPx = rowCount * circleOuterPx + rowGapPx * (rowCount - 1);
    const heightBudget =
        maxHeight != null && Number.isFinite(Number(maxHeight)) && Number(maxHeight) > 0
            ? Math.max(36, Number(maxHeight) - padY)
            : null;
    if (heightBudget != null && trayHeightPx > heightBudget) {
        const fit = heightBudget / trayHeightPx;
        trayScale *= fit;
        circleOuterPx = Math.max(28, Math.floor(circleOuterPx * fit));
        trayHeightPx = rowCount * circleOuterPx + rowGapPx * (rowCount - 1);
    }
    const columns = chunkTrayColumns(pieceIndices, rowCount);

    return (
        <Box
            sx={{
                ...(bare ? null : trayInnerSx),
                minHeight: bare ? trayHeightPx : trayHeightPx + 8,
                height: bare && heightBudget != null ? "100%" : undefined,
                boxSizing: "border-box",
                py: bare ? "2px" : 0.75,
                px: 0.5,
                display: "flex",
                alignItems: "center",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    minHeight: trayHeightPx,
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        alignItems: "flex-start",
                        width: "max-content",
                        minWidth: "100%",
                        height: trayHeightPx,
                        px: 1,
                        gap: "6px",
                    }}
                >
                    {columns.map((column) => (
                        <Box
                            key={column.join("-")}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: `${rowGapPx}px`,
                                flexShrink: 0,
                            }}
                        >
                            {column.map((pieceIndex) => (
                                <Box
                                    key={pieceIndex}
                                    sx={{
                                        flexShrink: 0,
                                        width: circleOuterPx,
                                        height: circleOuterPx,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        filter: "drop-shadow(0 2px 4px rgba(15,23,42,0.12))",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            transform: `scale(${trayScale})`,
                                            transformOrigin: "center center",
                                            lineHeight: 0,
                                        }}
                                    >
                                        <PieceVisual
                                            pieceIndex={pieceIndex}
                                            imageUrl={imageUrl}
                                            grid={grid}
                                            paths={paths}
                                            anchorToCell={false}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
